import type {
  HomeFeed,
  Item,
  ItemDetail,
  Library,
  LibraryFilter,
  LibrarySort,
  User,
} from './api.ts';
import { recordSwallowed } from './swallowed.ts';

/**
 * Çevrimdışı son katalog önbelleği (V6-03).
 *
 * Yalnız sunucunun katalog özeti ve kullanıcı kimliğiyle ad alanına alınmış
 * salt-okunur veri saklanır. Oturum çerezi, jeton, parola veya API yanıtı
 * burada tutulmaz.
 */
export interface OfflineLibraryPage {
  libraryId: string;
  offset: number;
  sort: LibrarySort;
  filter: LibraryFilter;
  items: Item[];
  total: number;
  savedAt: number;
}

export interface OfflineCatalog {
  userId: string;
  savedAt: number;
  /** Yalnız profil kimliği; oturum jetonu/çerezi asla saklanmaz. */
  user: User | null;
  libraries: Library[];
  home: HomeFeed | null;
  /** Görüntülenen kütüphane sayfaları; anahtar `offlineLibraryKey`. */
  libraryPages?: Record<string, OfflineLibraryPage>;
  /** Görüntülenen öğe detayları. */
  items?: Record<string, ItemDetail>;
}

const DB_NAME = 'pbfork-offline-catalog';
const STORE = 'catalog';
const ITEM_STORE = 'items';
// v2: öğe detayları ayrı store'da; tek öğe okumak tüm katalogu çözmez (G-31).
const VERSION = 2;
const LAST_USER_KEY = 'pbfork.offline.lastUser';

/**
 * Çevrimdışı katalog kaydının ömrü. Cihazda kalan izleme geçmişi ve katalog
 * süresiz tutulmaz; kullanıcı çıkış yapmasa bile eski kayıt düşer.
 */
export const OFFLINE_CATALOG_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Kayıt yaşı TTL'i aştıysa kullanılmaz. */
export function isOfflineCatalogFresh(
  savedAt: number,
  now = Date.now(),
  ttlMs = OFFLINE_CATALOG_TTL_MS,
): boolean {
  return Number.isFinite(savedAt) && now - savedAt <= ttlMs;
}

/** Aynı kütüphane sayfasının sıralama/filtre/sayfa başına anahtarı. */
export function offlineLibraryKey(
  libraryId: string,
  sort: LibrarySort,
  filter: LibraryFilter,
  offset: number,
): string {
  return `${libraryId}|${sort}|${filter}|${offset}`;
}

/**
 * API medya URL'lerindeki kısa ömürlü `token` parametresini temizler.
 * Çevrimdışı önbellek yalnız katalog verisi tutar; medya jetonu saklanmaz.
 */
export function stripMediaTokens<T>(value: T): T {
  if (typeof value === 'string') {
    if (!value.includes('token=')) return value;
    try {
      const absolute = /^https?:\/\//i.test(value);
      const parsed = new URL(value, 'https://pbfork.invalid');
      if (!parsed.pathname.startsWith('/api/')) return value;
      parsed.searchParams.delete('token');
      return (
        absolute ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`
      ) as T;
    } catch (error) {
      recordSwallowed('offlineCatalog', error);
      return value;
    }
  }
  if (Array.isArray(value)) {
    return value.map((entry) => stripMediaTokens(entry)) as T;
  }
  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      result[key] = stripMediaTokens(entry);
    }
    return result as T;
  }
  return value;
}

export function lastOfflineUserId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(LAST_USER_KEY);
  } catch (error) {
    recordSwallowed('offlineCatalog', error);
    return null;
  }
}

export function rememberOfflineUser(userId: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LAST_USER_KEY, userId);
  } catch (error) {
    recordSwallowed('offlineCatalog', error);
    // Özel mod: çevrimdışı kimlik tutulmaz.
  }
}

export function forgetOfflineUser(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(LAST_USER_KEY);
  } catch (error) {
    recordSwallowed('offlineCatalog', error);
    // yoksay
  }
}

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    const request = indexedDB.open(DB_NAME, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'userId' });
      }
      if (!db.objectStoreNames.contains(ITEM_STORE)) {
        db.createObjectStore(ITEM_STORE, { keyPath: ['userId', 'itemId'] });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function read(userId: string): Promise<OfflineCatalog | null> {
  const db = await openDb();
  if (db === null) return null;
  try {
    const tx = db.transaction(STORE, 'readonly');
    const value = (await requestResult(tx.objectStore(STORE).get(userId))) as
      | OfflineCatalog
      | undefined;
    if (value === undefined) return null;
    // Süresi dolmuş kayıt çevrimdışı olarak gösterilmez.
    if (!isOfflineCatalogFresh(value.savedAt)) return null;
    return stripMediaTokens(value);
  } catch (error) {
    recordSwallowed('offlineCatalog', error);
    return null;
  } finally {
    db.close();
  }
}

async function write(value: OfflineCatalog): Promise<void> {
  const db = await openDb();
  if (db === null) return;
  try {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    recordSwallowed('offlineCatalog', error);
    // Özel mod / kota: çevrimdışı özellik sessizce devre dışı kalır.
  } finally {
    db.close();
  }
}

/** Tek öğeyi ayrı store'a yazar; büyük anlık görüntüyü yeniden okumaz. */
async function writeItem(userId: string, item: ItemDetail): Promise<void> {
  const db = await openDb();
  if (db === null) return;
  try {
    const tx = db.transaction(ITEM_STORE, 'readwrite');
    tx.objectStore(ITEM_STORE).put({
      userId,
      itemId: item.id,
      item: stripMediaTokens(item),
      savedAt: Date.now(),
    });
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    recordSwallowed('offlineCatalog', error);
    // Öğe store yazılamazsa anlık görüntüdeki kopya yedek olarak kalır.
  } finally {
    db.close();
  }
}

async function readItem(userId: string, itemId: string): Promise<ItemDetail | null> {
  const db = await openDb();
  if (db === null) return null;
  try {
    const tx = db.transaction(ITEM_STORE, 'readonly');
    const value = (await requestResult(tx.objectStore(ITEM_STORE).get([userId, itemId]))) as
      | { item?: ItemDetail; savedAt?: number }
      | undefined;
    if (value?.item === undefined) return null;
    if (!isOfflineCatalogFresh(value.savedAt ?? 0)) return null;
    return stripMediaTokens(value.item);
  } catch (error) {
    recordSwallowed('offlineCatalog', error);
    return null;
  } finally {
    db.close();
  }
}

/**
 * Aynı anahtar için eşzamanlı kayıtlar birleştirilir: yalnız **en yeni** değer
 * yazılır. Farklı anahtarlar (ana sayfa, kütüphane, öğe) genel zincirde sırayla
 * çalışır; böylece oku-değiştir-yaz yarışı oluşmaz (G-30).
 */
let writeChain: Promise<void> = Promise.resolve();
const pendingWrites = new Map<string, () => Promise<void>>();
const activeWrites = new Map<string, Promise<void>>();

function serializedWrite(key: string, task: () => Promise<void>): Promise<void> {
  pendingWrites.set(key, task);
  const active = activeWrites.get(key);
  if (active !== undefined) return active;

  const result = writeChain
    .then(async () => {
      while (pendingWrites.has(key)) {
        const next = pendingWrites.get(key);
        pendingWrites.delete(key);
        if (next !== undefined) await next();
      }
    })
    .finally(() => {
      activeWrites.delete(key);
    });

  activeWrites.set(key, result);
  writeChain = result.catch(() => undefined);
  return result;
}

export async function loadOfflineCatalog(userId: string): Promise<OfflineCatalog | null> {
  return read(userId);
}

export function saveOfflineLibraryPage(
  userId: string,
  page: Omit<OfflineLibraryPage, 'savedAt'>,
): Promise<void> {
  const pageKey = offlineLibraryKey(page.libraryId, page.sort, page.filter, page.offset);
  return serializedWrite(`page:${userId}:${pageKey}`, async () => {
    const previous = await read(userId);
    await write({
      userId,
      user: previous?.user ?? null,
      libraries: previous?.libraries ?? [],
      home: previous?.home ?? null,
      libraryPages: {
        ...(previous?.libraryPages ?? {}),
        [pageKey]: { ...page, items: stripMediaTokens(page.items), savedAt: Date.now() },
      },
      items: previous?.items ?? {},
      savedAt: Date.now(),
    });
  });
}

export async function loadOfflineLibraryPage(
  userId: string,
  libraryId: string,
  sort: LibrarySort,
  filter: LibraryFilter,
  offset: number,
): Promise<OfflineLibraryPage | null> {
  const snapshot = await read(userId);
  if (snapshot === null) return null;
  const pages = snapshot.libraryPages ?? {};
  const exact = pages[offlineLibraryKey(libraryId, sort, filter, offset)];
  if (exact !== undefined) return exact;

  // İstenen sayfa önbellekte yoksa aynı kütüphanenin en yeni sayfasını kullan.
  return (
    Object.values(pages)
      .filter((page) => page.libraryId === libraryId)
      .sort((left, right) => right.savedAt - left.savedAt)[0] ?? null
  );
}

export function saveOfflineItem(userId: string, item: ItemDetail): Promise<void> {
  return serializedWrite(`item:${userId}:${item.id}`, async () => {
    const previous = await read(userId);
    await write({
      userId,
      user: previous?.user ?? null,
      libraries: previous?.libraries ?? [],
      home: previous?.home ?? null,
      libraryPages: previous?.libraryPages ?? {},
      items: { ...(previous?.items ?? {}), [item.id]: stripMediaTokens(item) },
      savedAt: Date.now(),
    });
    await writeItem(userId, item);
  });
}

export async function loadOfflineItem(userId: string, itemId: string): Promise<ItemDetail | null> {
  // Küçük store'dan tek kayıt: tüm katalog nesnesi çözülmez (G-31).
  const stored = await readItem(userId, itemId);
  if (stored !== null) return stored;

  const snapshot = await read(userId);
  if (snapshot === null) return null;
  const detail = snapshot.items?.[itemId];
  if (detail !== undefined) return detail;

  // Detay daha önce açılmamış olabilir; ana sayfa/kütüphane kartından
  // temel bir detay üret. Böylece çevrimdışıyken karta tıklanınca
  // "failed to fetch" yerine başlık/afiş/özet görünür.
  const item = findCachedCatalogItem(snapshot, itemId);
  return item === null ? null : { ...item, children: [], episodes: [] };
}

/** Ana sayfa raflarında veya kütüphane sayfalarında öğe arar. */
export function findCachedCatalogItem(snapshot: OfflineCatalog, itemId: string): Item | null {
  if (snapshot.home?.hero?.id === itemId) return snapshot.home.hero;
  for (const rail of snapshot.home?.rails ?? []) {
    const found = rail.items.find((item) => item.id === itemId);
    if (found !== undefined) return found;
  }
  for (const page of Object.values(snapshot.libraryPages ?? {})) {
    const found = page.items.find((item) => item.id === itemId);
    if (found !== undefined) return found;
  }
  return null;
}

export function saveOfflineLibraries(
  userId: string,
  libraries: Library[],
  user: User | null = null,
): Promise<void> {
  return serializedWrite(`libraries:${userId}`, async () => {
    const previous = await read(userId);
    rememberOfflineUser(userId);
    await write({
      userId,
      user: user ?? previous?.user ?? null,
      libraries: stripMediaTokens(libraries),
      home: previous?.home ?? null,
      libraryPages: previous?.libraryPages ?? {},
      items: previous?.items ?? {},
      savedAt: Date.now(),
    });
  });
}

export function saveOfflineHome(userId: string, home: HomeFeed): Promise<void> {
  return serializedWrite(`home:${userId}`, async () => {
    const previous = await read(userId);
    await write({
      userId,
      user: previous?.user ?? null,
      libraries: previous?.libraries ?? [],
      home: stripMediaTokens(home),
      libraryPages: previous?.libraryPages ?? {},
      items: previous?.items ?? {},
      savedAt: Date.now(),
    });
  });
}

export async function clearOfflineCatalog(userId?: string): Promise<void> {
  // Kimlik, IndexedDB'den bağımsız olarak unutulur: depolama kapalıysa bile
  // çıkış sonrası "son kullanıcı" çevrimdışı katalogu açmamalı.
  if (userId === undefined || lastOfflineUserId() === userId) forgetOfflineUser();
  const db = await openDb();
  if (db === null) return;
  try {
    const tx = db.transaction([STORE, ITEM_STORE], 'readwrite');
    if (userId === undefined) {
      tx.objectStore(STORE).clear();
      tx.objectStore(ITEM_STORE).clear();
    } else {
      tx.objectStore(STORE).delete(userId);
      // Bileşik anahtar aralığı: [userId, ''] … [userId, '\uffff']
      tx.objectStore(ITEM_STORE).delete(IDBKeyRange.bound([userId, ''], [userId, '\uffff']));
    }
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    recordSwallowed('offlineCatalog', error);
    // Temizlik başarısızsa bir sonraki başarılı çıkışta tekrar denenir.
  } finally {
    db.close();
  }
}
